import { motion } from "framer-motion";
import { MapPin, Clock, Phone, ChevronRight } from "lucide-react";

const locations = [
  {
    name: "Flexanville",
    address: "23 rue de Goupillières",
    city: "78910 FLEXANVILLE",
    phone: "06 64 44 68 23",
    hours: "Lun - Sam : 9h - 19h",
    description: "Notre centre principal avec des salles modernes et équipées.",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2628.5!2d1.7878!3d48.8456!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e6a1c5c5c5c5c5%3A0x0!2s23%20Rue%20de%20Goupilli%C3%A8res%2C%2078910%20Flexanville!5e0!3m2!1sfr!2sfr!4v1"
  },
  {
    name: "La Queue-lez-Yvelines",
    address: "Centre ville",
    city: "78940 LA QUEUE-LEZ-YVELINES",
    phone: "06 64 44 68 23",
    hours: "Lun - Sam : 9h - 19h",
    description: "Un espace chaleureux pour un accompagnement de proximité.",
    mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2628.5!2d1.7878!3d48.8056!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e6a1c5c5c5c5c5%3A0x0!2sLa%20Queue-lez-Yvelines!5e0!3m2!1sfr!2sfr!4v1"
  }
];

export const Locations = () => {
  return (
    <section
      id="centres"
      data-testid="locations-section"
      className="py-14 md:py-28 relative bg-blue-50/50"
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <span className="inline-block text-blue-600 font-semibold mb-4 tracking-wider uppercase text-sm bg-blue-100 px-4 py-2 rounded-full">
            Nos Centres
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl tracking-tight font-bold text-blue-900 mb-4 md:mb-6">
            Deux centres à votre service
          </h2>
          <p className="text-base md:text-lg text-blue-800/70 max-w-2xl mx-auto">
            Retrouvez-nous dans nos deux centres situés dans les Yvelines 
            pour un accompagnement de proximité.
          </p>
        </motion.div>

        {/* Location Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
          {locations.map((location, index) => (
            <motion.div
              key={location.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              data-testid={`location-card-${index}`}
              className="location-card bg-white rounded-3xl overflow-hidden shadow-soft border border-blue-100"
            >
              {/* Google Map */}
              <div className="h-48 bg-gray-100 relative overflow-hidden">
                <iframe
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(location.address + ', ' + location.city)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Carte ${location.name}`}
                  className="grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-6 md:p-8">
                <h3 className="text-2xl font-bold text-blue-900 mb-2">
                  {location.name}
                </h3>
                <p className="text-blue-600/60 text-sm mb-6">
                  {location.description}
                </p>
                
                <div className="space-y-4">
                  {/* Address */}
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-blue-900 font-medium">{location.address}</p>
                      <p className="text-blue-600/60 text-sm">{location.city}</p>
                    </div>
                  </div>

                  {/* Hours */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-green-600" />
                    </div>
                    <p className="text-blue-900">{location.hours}</p>
                  </div>

                  {/* Phone */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-orange-600" />
                    </div>
                    <a 
                      href={`tel:${location.phone.replace(/\s/g, '')}`}
                      className="text-blue-900 font-medium hover:text-orange-500 transition-colors"
                    >
                      {location.phone}
                    </a>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <a
                    href="#contact"
                    data-testid={`location-cta-${index}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-xl font-medium transition-all hover:shadow-lg"
                  >
                    <span>Nous contacter</span>
                    <ChevronRight className="w-5 h-5" />
                  </a>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location.address + ', ' + location.city)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`location-directions-${index}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 py-3 px-6 rounded-xl font-medium transition-all"
                  >
                    <MapPin className="w-5 h-5" />
                    <span>Itinéraire</span>
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Main Address Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 bg-white border border-blue-200 rounded-2xl sm:rounded-full px-5 py-3 shadow-soft mx-auto">
            <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0" />
            <span className="text-blue-900 text-sm sm:text-base">
              <strong>Siège social :</strong> 23 rue de Goupillières, 78910 FLEXANVILLE
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
